import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../components/molecules';

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  patientName: string;
  ownerName: string;
  date: string;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  total: number;
  notes: string;
}

const invoiceStatuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' }
] as const;

const statusStyles: Record<
  Invoice['status'],
  { badge: string; label: string; indicator: string }
> = {
  draft: {
    badge: 'bg-slate-200 text-slate-700 border border-slate-300',
    label: 'Draft',
    indicator: 'bg-slate-400'
  },
  sent: {
    badge: 'bg-sky-100 text-sky-600 border border-sky-200',
    label: 'Sent',
    indicator: 'bg-sky-400'
  },
  paid: {
    badge: 'bg-emerald-100 text-emerald-600 border border-emerald-200',
    label: 'Paid',
    indicator: 'bg-emerald-400'
  },
  overdue: {
    badge: 'bg-rose-100 text-rose-600 border border-rose-200',
    label: 'Overdue',
    indicator: 'bg-rose-400'
  }
};

const commonServices = [
  'Regular Checkup',
  'Vaccination',
  'Emergency Visit',
  'Dental Cleaning',
  'Surgery',
  'X-Ray',
  'Blood Work',
  'Hospitalization',
  'Grooming',
  'Boarding'
];

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([
    {
      id: '1',
      invoiceNumber: 'INV-2024-001',
      patientName: 'Max',
      ownerName: 'John Doe',
      date: '2024-01-10',
      dueDate: '2024-01-24',
      status: 'sent',
      items: [
        { description: 'Regular Checkup', quantity: 1, unitPrice: 75, total: 75 },
        { description: 'Vaccination (DHPP)', quantity: 1, unitPrice: 45, total: 45 },
        { description: 'Heartgard Plus (6 months)', quantity: 1, unitPrice: 30, total: 30 }
      ],
      subtotal: 150,
      tax: 15,
      total: 165,
      notes: 'Payment due within 14 days'
    },
    {
      id: '2',
      invoiceNumber: 'INV-2024-002',
      patientName: 'Luna',
      ownerName: 'Jane Smith',
      date: '2024-01-08',
      dueDate: '2024-01-22',
      status: 'paid',
      items: [
        { description: 'Emergency Visit', quantity: 1, unitPrice: 150, total: 150 },
        { description: 'IV Fluid Therapy', quantity: 1, unitPrice: 125, total: 125 },
        { description: 'Medications', quantity: 1, unitPrice: 75, total: 75 },
        { description: 'Hospitalization (1 day)', quantity: 1, unitPrice: 100, total: 100 }
      ],
      subtotal: 450,
      tax: 45,
      total: 495,
      notes: 'Paid via credit card on 2024-01-15'
    }
  ]);

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Invoice['status']>('all');

  const [formData, setFormData] = useState({
    patientName: '',
    ownerName: '',
    date: '',
    dueDate: '',
    notes: '',
    items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }]
  });

  const filteredInvoices = useMemo(
    () =>
      invoices.filter((invoice) => {
        const matchesSearch =
          invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          invoice.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          invoice.ownerName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
        return matchesSearch && matchesStatus;
      }),
    [invoices, searchTerm, statusFilter]
  );

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unitPrice: 0, total: 0 }]
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: string | number) => {
    setFormData((prev) => {
      const items = [...prev.items];
      if (field === 'description') {
        items[index][field] = value as string;
      } else {
        items[index][field] = parseFloat(String(value)) || 0;
      }
      items[index].total = items[index].quantity * items[index].unitPrice;
      return { ...prev, items };
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * 0.1;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { subtotal, tax, total } = calculateTotals();

    const newInvoice: Invoice = {
      id: Date.now().toString(),
      invoiceNumber: `INV-${new Date().getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`,
      ...formData,
      subtotal,
      tax,
      total,
      status: 'draft'
    };

    setInvoices((prev) => [...prev, newInvoice]);
    setFormData({
      patientName: '',
      ownerName: '',
      date: '',
      dueDate: '',
      notes: '',
      items: [{ description: '', quantity: 1, unitPrice: 0, total: 0 }]
    });
    setShowForm(false);
  };

  const handleStatusChange = (id: string, newStatus: Invoice['status']) => {
    setInvoices((prev) => prev.map((invoice) => (invoice.id === id ? { ...invoice, status: newStatus } : invoice)));
  };

  const handleDelete = (id: string) => {
    setInvoices((prev) => prev.filter((invoice) => invoice.id !== id));
  };

  const { subtotal, tax, total } = calculateTotals();
  const outstandingTotal = useMemo(
    () =>
      invoices
        .filter((invoice) => invoice.status !== 'paid')
        .reduce((sum, invoice) => sum + invoice.total, 0),
    [invoices]
  );
  const paidCount = useMemo(() => invoices.filter((invoice) => invoice.status === 'paid').length, [invoices]);
  const filtersActive = Boolean(searchTerm) || statusFilter !== 'all';
  const filterSummary = filtersActive
    ? [
        searchTerm ? `Keyword: "${searchTerm}"` : null,
        statusFilter !== 'all' ? `Status: ${statusStyles[statusFilter].label}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Showing every invoice';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        description="Track billing, payment status, and outstanding balances at a glance."
        icon="🧾"
        accent="violet"
        actions={(
          <button
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-content shadow-lg transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            onClick={() => setShowForm(true)}
          >
            <span className="text-lg leading-none">➕</span>
            Create invoice
          </button>
        )}
        stats={[
          { label: 'Total invoices', value: invoices.length, helper: 'All time' },
          {
            label: 'Outstanding',
            value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(outstandingTotal),
            helper: 'Not yet paid'
          },
          { label: 'Paid', value: paidCount, helper: 'Cleared invoices' }
        ]}
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
            Search invoices
            <input
              type="text"
              placeholder="Search by invoice, patient, or owner…"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
            Status
            <select
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="all">All statuses</option>
              {invoiceStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="mt-3 text-xs text-slate-500">{filterSummary}</p>
      </PageHeader>

      <section className="space-y-4">
        {filteredInvoices.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-brand-muted bg-white/70 px-8 py-16 text-center shadow-soft">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/10 text-3xl text-brand-primary">
              💳
            </div>
            <h3 className="mt-6 text-lg font-semibold text-slate-700">No invoices found</h3>
            <p className="mt-2 text-sm text-slate-500">
              Create a new invoice or adjust your filters to see matching records.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {filteredInvoices.map((invoice) => {
              const status = statusStyles[invoice.status];
              return (
                <article
                  key={invoice.id}
                  className="flex h-full flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <header className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Invoice
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-slate-800">{invoice.invoiceNumber}</h3>
                      <p className="text-sm text-slate-500">
                        Issued {new Date(invoice.date).toLocaleDateString('en-US')}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.badge}`}>
                        {status.label}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Due {new Date(invoice.dueDate).toLocaleDateString('en-US')}
                      </span>
                    </div>
                  </header>

                  <div className="grid gap-4 rounded-2xl border border-slate-100 bg-slate-50/80 p-4 text-sm">
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="rounded-full bg-brand-secondary/10 px-3 py-1 text-xs font-semibold text-brand-secondary">
                        Patient: {invoice.patientName}
                      </span>
                      <span className="rounded-full bg-brand-accent/10 px-3 py-1 text-xs font-semibold text-brand-accent">
                        Owner: {invoice.ownerName}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {invoice.items.map((item, index) => (
                        <div key={`${invoice.id}-item-${index}`} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-700">{item.description}</p>
                            <p className="text-xs text-slate-500">
                              {item.quantity} ×{' '}
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                              }).format(item.unitPrice)}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-slate-800">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            }).format(item.total)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-sm">
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Subtotal</span>
                      <span>
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        }).format(invoice.subtotal)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>Tax (10%)</span>
                      <span>
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        }).format(invoice.tax)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-lg font-semibold text-slate-800">
                      <span>Total</span>
                      <span>
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        }).format(invoice.total)}
                      </span>
                    </div>
                  </div>

                  {invoice.notes && (
                    <p className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      <span className="font-semibold text-slate-500">Notes:</span> {invoice.notes}
                    </p>
                  )}

                  <footer className="flex flex-wrap items-center gap-2">
                    {invoice.status === 'draft' && (
                      <button
                        className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-xs font-semibold text-sky-600 transition hover:bg-sky-100"
                        onClick={() => handleStatusChange(invoice.id, 'sent')}
                      >
                        ✉️ Send invoice
                      </button>
                    )}
                    {invoice.status === 'sent' && (
                      <button
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-100"
                        onClick={() => handleStatusChange(invoice.id, 'paid')}
                      >
                        ✅ Mark paid
                      </button>
                    )}
                    {invoice.status !== 'paid' && (
                      <button
                        className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100"
                        onClick={() => handleDelete(invoice.id)}
                      >
                        🗑 Delete
                      </button>
                    )}
                    <span className={`ml-auto h-2 w-10 rounded-full ${status.indicator}`} />
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
            <button
              className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-500 transition hover:bg-slate-100"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >
              ✕
            </button>

            <header>
              <h2 className="text-xl	font-semibold text-slate-800">Create invoice</h2>
              <p className="mt-2 text-sm text-slate-500">
                Capture services rendered and generate a professional invoice instantly.
              </p>
            </header>

            <form className="mt-6 space-y-4 text-sm" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Patient name *
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.patientName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, patientName: e.target.value }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Owner name *
                  <input
                    type="text"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.ownerName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ownerName: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Invoice date *
                  <input
                    type="date"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </label>
                <label className="flex flex-col gap-2 font-semibold text-slate-600">
                  Due date *
                  <input
                    type="date"
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                    value={formData.dueDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
                    required
                  />
                </label>
              </div>

              <div className="space-y-4 rounded-3xl border border-slate-100 bg-slate-50/80 p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">Invoice items</h3>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    onClick={handleAddItem}
                  >
                    ➕ Add item
                  </button>
                </div>

                {formData.items.map((item, index) => (
                  <div key={index} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))_auto]">
                      <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Description
                        <input
                          type="text"
                          required
                          placeholder="e.g., Regular checkup"
                          list="services"
                          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        />
                      </label>
                      <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Qty
                        <input
                          type="number"
                          min="1"
                          required
                          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        />
                      </label>
                      <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Unit price
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          required
                          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        />
                      </label>
                      <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                        Total
                        <input
                          type="text"
                          disabled
                          className="rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500"
                          value={new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          }).format(item.total)}
                        />
                      </label>
                      <div className="flex items-end justify-end">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => handleRemoveItem(index)}
                          disabled={formData.items.length === 1}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <datalist id="services">
                {commonServices.map((service) => (
                  <option key={service} value={service} />
                ))}
              </datalist>

              <div className="rounded-3xl border border-slate-100 bg-white px-6 py-4 text-sm">
                <div className="flex items-center justify-between text-slate-500">
                  <span>Subtotal</span>
                  <span>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(subtotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-slate-500">
                  <span>Tax (10%)</span>
                  <span>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tax)}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between text-lg font-semibold text-slate-800">
                  <span>Total</span>
                  <span>
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total)}
                  </span>
                </div>
              </div>

              <label className="flex flex-col gap-2 font-semibold text-slate-600">
                Notes
                <textarea
                  rows={3}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add payment instructions or friendly reminders."
                />
              </label>

              <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-full bg-brand-primary px-6 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                >
                  Save invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
